import React from 'react';
import PageMeta from '../components/PageMeta';

const sections = [
  {
    title: '1. Subscription Cancellation',
    body: [
      'Subscribers may cancel their subscription at any time through their account settings or by contacting support.',
      'Cancellation prevents future renewal charges.',
    ],
  },
  {
    title: '2. Access After Cancellation',
    body: [
      'When a subscription is cancelled:',
    ],
    bullets: [
      'Access remains active until the end of the current billing period.',
      'No further renewal charges will be applied.',
      'Existing content access remains available until subscription expiry.',
    ],
    example: 'If a subscriber purchases an annual membership on 1 July 2026 and cancels on 15 March 2027, access remains active until 30 June 2027.',
  },
  {
    title: '3. Auto-Renewal',
    body: [
      'Subscriptions may renew automatically at the end of the billing cycle unless cancelled before the renewal date.',
      'Renewal reminders may be sent before renewal.',
    ],
  },
  {
    title: '4. No Partial Refunds',
    body: [
      'Cancellation does not entitle the subscriber to:',
    ],
    bullets: [
      'Full refunds,',
      'Partial refunds,',
      'Prorated refunds,',
    ],
    closing: 'except where required by law or specifically approved under the Refund Policy.',
  },
  {
    title: '5. Subscription Upgrades',
    body: [
      'Subscribers may upgrade their plan at any time.',
      'Any unused value from the existing subscription may be applied as credit toward the upgraded plan.',
      'Upgraded access becomes available immediately upon successful payment.',
    ],
  },
  {
    title: '6. Subscription Downgrades',
    body: [
      'Downgrades generally take effect at the next renewal date.',
      'The subscriber retains access to the existing plan benefits until the current subscription period expires.',
    ],
  },
  {
    title: '7. Changes',
    body: [
      'BTree reserves the right to amend this Cancellation Policy at any time. Updated versions will be published on the website.',
    ],
  },
];

const CancellationPolicy = () => {
  return (
    <>
      <PageMeta
        title="Cancellation Policy | The B Tree"
        description="Understand B Tree subscription cancellation terms, renewal timing, and access continuity after cancellation."
        canonical="https://www.thebtree.com/cancellation"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <main className="min-h-screen bg-[#F7F5F2]" data-testid="cancellation-policy-page">
      <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-18">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
            BTree Policies
          </p>
          <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-4">
            TheBTree Cancellation Policy
          </h1>
          <p className="text-sm md:text-base text-slate-600">
            Effective Date: June 15, 2026
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <p className="text-lg leading-8 text-slate-700 mb-12">
            This Cancellation Policy applies to all subscriptions purchased through The BTree Company.
          </p>

          <div className="space-y-10">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-2xl md:text-3xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-3">
                  {section.title}
                </h2>

                {section.body.map((paragraph) => (
                  <p key={paragraph} className="text-slate-700 leading-7 mb-4">
                    {paragraph}
                  </p>
                ))}

                {section.bullets && (
                  <ul className="space-y-2 text-slate-700 leading-7">
                    {section.bullets.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-3 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#C8A84B]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.example && (
                  <div className="mt-5 border-l-2 border-[#C8A84B] bg-[#F7F5F2] px-5 py-4">
                    <p className="text-xs font-mono tracking-[0.2em] uppercase text-[#9B7A21] mb-2">
                      Example
                    </p>
                    <p className="text-slate-700 leading-7">
                      {section.example}
                    </p>
                  </div>
                )}

                {section.closing && (
                  <p className="text-slate-700 leading-7 mt-4">
                    {section.closing}
                  </p>
                )}
              </section>
            ))}
          </div>
        </article>
      </section>
    </main>
    </>
  );
};

export default CancellationPolicy;
